import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Breadcrumb, Card, Statistic, Row, Col, Input, Tag, Spin, message, Empty, Select } from 'antd';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    TrophyOutlined,
    SearchOutlined,
    ArrowLeftOutlined,
    CalendarOutlined,
    TeamOutlined,
    DollarOutlined,
    RightOutlined,
    StarOutlined,
    CrownOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { rankingsService } from '../../common/services';
import dayjs from 'dayjs';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
};

const formatPeriod = (p) => {
    if (!p) return '';
    const [year, month] = p.split('-');
    const months = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
};

const getRankBadgeStyle = (rank) => {
    if (rank === 1) return { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: 'white' };
    if (rank === 2) return { background: 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)', color: 'white' };
    if (rank === 3) return { background: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)', color: 'white' };
    return { background: '#f0f0f0', color: '#666' };
};

const RankingsDetail = () => {
    const { period } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Récupérer la ville depuis l'URL
    const cityFromUrl = searchParams.get('city');

    const [rankings, setRankings] = useState([]);
    const [top15, setTop15] = useState([]);
    const [stats, setStats] = useState(null);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(cityFromUrl || null);
    const [isLoading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

    // Charger les villes disponibles
    const loadCities = useCallback(async () => {
        try {
            const response = await rankingsService.getCities();
            if (response.success) {
                const loadedCities = response.data.cities || [];
                setCities(loadedCities);
                // Si aucune ville sélectionnée, auto-sélectionner la ville par défaut
                if (!selectedCity) {
                    const defaultCity = loadedCities.find(c => c.isDefault);
                    if (defaultCity) {
                        setSelectedCity(defaultCity._id);
                        setSearchParams({ city: defaultCity._id });
                    }
                }
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search
            };
            if (selectedCity) {
                params.city = selectedCity;
            }

            const response = await rankingsService.getRankingByPeriod(period, params);

            if (response.success) {
                setRankings(response.data.rankings || []);
                setTop15(response.data.top15 || []);
                setStats(response.data.stats);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination?.total || 0
                }));
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [period, pagination.page, pagination.limit, search, selectedCity]);

    useEffect(() => {
        loadCities();
    }, [loadCities]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Mettre à jour l'URL quand la ville change
    const handleCityChange = (value) => {
        setSelectedCity(value);
        setPagination(prev => ({ ...prev, page: 1 }));
        if (value) {
            setSearchParams({ city: value });
        } else {
            setSearchParams({});
        }
    };

    const handleTableChange = (pag) => {
        setPagination(prev => ({
            ...prev,
            page: pag.current,
            limit: pag.pageSize
        }));
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const columns = [
        {
            title: 'Rang',
            dataIndex: 'rank',
            key: 'rank',
            width: 80,
            render: (rank) => (
                <div
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: 16,
                        ...getRankBadgeStyle(rank)
                    }}
                >
                    {rank}
                </div>
            )
        },
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {record.firstName || 'Anonyme'} {record.lastName || ''}
                        {record.isTop15 && (
                            <Tag color="gold" style={{ marginLeft: 8 }}>
                                <StarOutlined /> Top 15
                            </Tag>
                        )}
                        {record.isTop5Quarterly && (
                            <Tag color="purple" style={{ marginLeft: 4 }}>
                                <CrownOutlined /> Top 5 Trim.
                            </Tag>
                        )}
                    </div>
                    <div style={{ color: '#999', fontSize: 13 }}>{record.phone}</div>
                </div>
            )
        },
        {
            title: 'Ville',
            dataIndex: 'cityName',
            key: 'cityName',
            render: (cityName) => cityName ? (
                <Tag icon={<EnvironmentOutlined />} color="blue">
                    {cityName}
                </Tag>
            ) : (
                <span style={{ color: '#999' }}>-</span>
            )
        },
        {
            title: 'ID Yandex',
            dataIndex: 'driverProfilId',
            key: 'driverProfilId',
            render: (id) => (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {id?.slice(0, 12)}...
                </span>
            )
        },
        {
            title: 'Revenus',
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            sorter: (a, b) => a.totalRevenue - b.totalRevenue,
            render: (amount) => (
                <span style={{ color: '#D4A853', fontWeight: 700, fontSize: 15 }}>
                    {formatCurrency(amount)}
                </span>
            )
        },
        {
            title: 'Courses',
            dataIndex: 'totalOrders',
            key: 'totalOrders',
            sorter: (a, b) => a.totalOrders - b.totalOrders
        },
        {
            title: 'Calcule le',
            dataIndex: 'calculatedAt',
            key: 'calculatedAt',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
        }
    ];

    return (
        <div className="rankings-detail-page">
            {/* Breadcrumb */}
            <Breadcrumb
                separator={<RightOutlined />}
                items={[
                    { title: <Link to="/dashboard">Tableau de bord</Link> },
                    { title: <Link to="/dashboard/rankings">Classement</Link> },
                    { title: formatPeriod(period) }
                ]}
                style={{ marginBottom: 24 }}
            />

            {/* Back Button & Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/dashboard/rankings')}
                >
                    Retour
                </Button>
                <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrophyOutlined style={{ color: '#D4A853' }} />
                    Classement {formatPeriod(period)}
                    {selectedCity && cities.find(c => c._id === selectedCity) && (
                        <Tag color="blue" style={{ marginLeft: 12 }}>
                            <EnvironmentOutlined /> {cities.find(c => c._id === selectedCity)?.name}
                        </Tag>
                    )}
                </h2>
            </div>

            {/* Filtre par ville */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <EnvironmentOutlined style={{ fontSize: 20, color: '#D4A853' }} />
                    <span style={{ fontWeight: 600 }}>Filtrer par ville :</span>
                    <Select
                        placeholder="Toutes les villes"
                        style={{ width: 250 }}
                        allowClear
                        value={selectedCity}
                        onChange={handleCityChange}
                        loading={cities.length === 0}
                    >
                        {cities.map(city => (
                            <Select.Option key={city._id} value={city._id}>
                                {city.name} {city.isDefault && '(Par défaut)'}
                            </Select.Option>
                        ))}
                    </Select>
                    {selectedCity && (
                        <Tag color="blue" icon={<EnvironmentOutlined />}>
                            {cities.find(c => c._id === selectedCity)?.name || 'Ville sélectionnée'}
                        </Tag>
                    )}
                </div>
            </Card>

            {/* Stats Cards - masquées pour le moment
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs"
                            value={stats?.driversCount || 0}
                            prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Revenus Total"
                            value={formatCurrency(stats?.totalRevenue)}
                            prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ fontSize: 18 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Courses Total"
                            value={stats?.totalOrders || 0}
                            prefix={<CalendarOutlined style={{ color: '#faad14' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Revenu Moyen"
                            value={formatCurrency(stats?.avgRevenue)}
                            prefix={<TrophyOutlined style={{ color: '#D4A853' }} />}
                            valueStyle={{ fontSize: 18 }}
                        />
                    </Card>
                </Col>
            </Row>
            */}

            {/* Top 15 Highlight */}
            {top15.length > 0 && (
                <Card
                    title={
                        <span>
                            <StarOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                            Top 15 - Recompenses Mensuelles
                        </span>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[12, 12]}>
                        {top15.map((driver) => (
                            <Col xs={24} sm={12} md={8} lg={6} key={driver._id}>
                                <Card
                                    size="small"
                                    style={{
                                        borderLeft: driver.rank <= 3 ? '4px solid #D4A853' : undefined
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 'bold',
                                                fontSize: 14,
                                                ...getRankBadgeStyle(driver.rank)
                                            }}
                                        >
                                            {driver.rank}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {driver.firstName} {driver.lastName?.charAt(0)}.
                                            </div>
                                            <div style={{ color: '#D4A853', fontWeight: 600, fontSize: 13 }}>
                                                {formatCurrency(driver.totalRevenue)}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Full Ranking Table */}
            <Card
                title={
                    <span>
                        <TeamOutlined style={{ marginRight: 8 }} />
                        Tous les chauffeurs ({pagination.total})
                    </span>
                }
                extra={
                    <Input.Search
                        placeholder="Rechercher..."
                        allowClear
                        style={{ width: 250 }}
                        onSearch={handleSearch}
                        prefix={<SearchOutlined />}
                    />
                }
            >
                <Table
                    dataSource={rankings}
                    columns={columns}
                    rowKey="_id"
                    loading={isLoading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => record.isTop15 ? 'top15-row' : ''}
                />
            </Card>

            <style>{`
                .top15-row {
                    background: linear-gradient(90deg, rgba(212, 168, 83, 0.08) 0%, transparent 100%);
                }
            `}</style>
        </div>
    );
};

export default RankingsDetail;
